/**
 * ProgressTracker — a horizontal step rail: dots connected by a line, a solid
 * green fill + checkmarks for completed steps, and a pulsing dot for the
 * current step.
 *
 * NOT a port of upstream's own `OrderStatusStepper`
 * (`tw-components.jsx:919`), even though that component does the same job —
 * checked before writing this, and upstream's version is visually a
 * different design: 44px dots, an **orange** current-step ring
 * (`dnd-os-pulse`), and a `check`/`circle` Material icon inside solid dots.
 * This one instead extracts the `.rma-track`/`.rma-track__fill` CSS and the
 * `v2Pulse`/`dnd-check` keyframes already used by this account's own
 * order-tracking anatomy (`Dens N Dente Tailwind.dc.html`'s ORDER DETAIL /
 * TRACKING screen — 26px dots, **blue** current-step pulse, a stroke-drawn
 * checkmark), which was already built, shown, and approved across several
 * rounds of review on the order-detail page before this component existed.
 * Swapping to upstream's version now would be a visual regression against
 * everything already signed off, not a fix — so this is a deliberate,
 * pre-existing-account-style component, extracted from the order-detail
 * page's per-shipment rail so any feature needing an N-step tracker in
 * *this* visual language can reuse it instead of re-copying the markup.
 * Reach for upstream's `OrderStatusStepper` instead if a future screen wants
 * upstream's own (orange/44px) styling, rather than trying to reuse this one.
 *
 * Dot spacing is set via inline `gridTemplateColumns` rather than a
 * `grid-cols-N` Tailwind class — Tailwind's JIT only emits classes it can see
 * literally in source, so a template-string class built from a dynamic
 * `steps.length` would silently render unstyled for any step count this
 * component's own callers don't already use verbatim elsewhere.
 */
import { cx } from "./cx";
import { Icon } from "./Icon.jsx";
import "./keyframes";

function TrackerCheck() {
  return (
    <svg
      className="dnd-check"
      width="12"
      height="12"
      viewBox="0 0 52 52"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M11 27.5 L22 38 L41 15"
        stroke="#fff"
        strokeWidth="9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * @param {number} [currentStep=0] - index (0-based) of the current step.
 *   Ignored when `variant="pending"`. `currentStep >= steps.length` means
 *   every step is done.
 * @param {string[]} steps - one label per dot, left to right.
 * @param {"progress"|"pending"} [variant="progress"] - "progress" is the
 *   normal rail (solid fill line, checkmarks for done steps, a pulsing
 *   current-step dot). "pending" is for something that hasn't started at
 *   all yet — a dashed line, an hourglass replacing the first dot (with no
 *   label under it, since nothing has actually happened), no fill, and no
 *   dot ever reads as done/current.
 */
export function ProgressTracker({
  currentStep = 0,
  steps,
  variant = "progress",
}) {
  const pending = variant === "pending";
  const total = steps.length;
  const fillPct =
    !pending && total > 1
      ? (Math.min(total - 1, currentStep) / (total - 1)) * 75
      : 0;

  return (
    <div className="relative">
      <div className="rma-track">
        <span
          className={cx(
            "absolute left-[12.5%] right-[12.5%] top-[13px]",
            pending
              ? "h-0 border-t-[2px] border-dashed border-neutral-300"
              : "h-[4px] rounded-pill bg-neutral-200 shadow-[inset_0_1px_2px_rgba(16,38,59,0.10)]"
          )}
        />
        {!pending && (
          <span
            className="rma-track__fill absolute left-[12.5%] top-[13px] h-[4px] rounded-pill bg-success-500 shadow-[0_1px_6px_-1px_rgba(34,153,84,0.55)]"
            style={{ width: `${fillPct}%` }}
          />
        )}
        <div
          className="relative grid"
          style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}
        >
          {steps.map((label, i) => {
            const isPendingDot = pending && i === 0;
            const done = !pending && i < currentStep;
            const current = !pending && i === currentStep;
            const dotCls = isPendingDot
              ? "bg-card border-dashed border-line text-ink-3"
              : done
                ? "bg-success-500 border-success-500 text-white"
                : current
                  ? "bg-card border-blue-600 text-blue-700 animate-[v2Pulse_2.2s_ease-out_infinite]"
                  : "bg-card border-line text-ink-3";
            const lblCls = done
              ? "text-success-700 font-bold"
              : current
                ? "text-blue-700 font-bold"
                : "text-ink-3 font-semibold";
            return (
              <div
                key={label}
                className="flex flex-col items-center gap-[10px]"
              >
                <span
                  className={cx(
                    "w-[26px] h-[26px] rounded-full border-[2px] inline-flex items-center justify-center shrink-0 [box-sizing:border-box] shadow-sm",
                    dotCls
                  )}
                >
                  {isPendingDot ? (
                    <Icon name="hourglass_top" size={14} />
                  ) : done ? (
                    <TrackerCheck />
                  ) : (
                    <span className="text-2xs font-bold">{i + 1}</span>
                  )}
                </span>
                {!isPendingDot && (
                  <span className={cx("text-2xs text-center", lblCls)}>
                    {label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
